import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../../api/client.js';
import type { StallSettings } from '../../../shared/stall.js';
import { Alert, Button, Card, Field, PageHeader, Spinner, TextInput } from '../../components/ui.js';

interface RunResult {
  checked: number;
  reminded: { workOrderNumber: string; stage: string; recipients: number }[];
}

const FIELDS: { key: keyof StallSettings; label: string; hint: string }[] = [
  {
    key: 'scheduleAfterBusinessDays',
    label: 'Approved but not scheduled — remind after (business days)',
    hint: 'USPS has approved; VGS has not entered a scheduled date.',
  },
  {
    key: 'completeAfterDays',
    label: 'Work in progress with no completion — remind after (days)',
    hint: 'Work was started; no completion date has been recorded.',
  },
  {
    key: 'closeAfterDays',
    label: 'Completed but not closed — remind after (days)',
    hint: 'Completion was recorded; the work order has not been closed.',
  },
  {
    key: 'uspsDecisionAfterDays',
    label: 'Awaiting USPS decision — remind the approver after (days)',
    hint: 'A proposal is submitted; the COR (or CO) has not recorded a decision.',
  },
  {
    key: 'repeatEveryDays',
    label: 'Repeat reminders every (days)',
    hint: 'How often the same reminder is re-sent until the step is done.',
  },
];

/**
 * Thresholds for the daily stall check. A work order whose scheduled date has
 * passed without work starting is reminded the next morning regardless.
 */
export function AdminRemindersTab() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Record<keyof StallSettings, string> | null>(null);
  const [runResult, setRunResult] = useState<RunResult | null>(null);

  const settings = useQuery({
    queryKey: ['admin', 'reminder-settings'],
    queryFn: () => apiRequest<StallSettings>('/api/admin/reminder-settings'),
  });

  useEffect(() => {
    if (settings.data && !form) {
      setForm({
        scheduleAfterBusinessDays: String(settings.data.scheduleAfterBusinessDays),
        completeAfterDays: String(settings.data.completeAfterDays),
        closeAfterDays: String(settings.data.closeAfterDays),
        uspsDecisionAfterDays: String(settings.data.uspsDecisionAfterDays),
        repeatEveryDays: String(settings.data.repeatEveryDays),
      });
    }
  }, [settings.data, form]);

  const save = useMutation({
    mutationFn: (values: StallSettings) =>
      apiRequest<StallSettings>('/api/admin/reminder-settings', { method: 'PUT', body: values }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reminder-settings'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'The settings could not be saved.'),
  });

  const run = useMutation({
    mutationFn: () => apiRequest<RunResult>('/api/admin/reminder-settings/run', { method: 'POST' }),
    onSuccess: (result) => setRunResult(result),
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'The check could not be run.'),
  });

  if (settings.isLoading || !form) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Workflow reminders"
        subtitle="Every morning the system finds work orders that have sat in one status too long and emails whoever owes the next step — the VGS staff for that facility, or the USPS approver — repeating until the step is recorded. Stalled work orders are also flagged on the Work orders list."
      />
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      <Card title="Thresholds">
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            const values = Object.fromEntries(
              FIELDS.map((field) => [field.key, Number(form[field.key])]),
            ) as unknown as StallSettings;
            if (Object.values(values).some((value) => !Number.isInteger(value) || value < 0)) {
              setError('Enter whole numbers of days.');
              return;
            }
            save.mutate(values);
          }}
        >
          {FIELDS.map((field) => (
            <Field key={field.key} label={field.label} htmlFor={field.key} hint={field.hint}>
              <TextInput
                id={field.key}
                type="number"
                min={field.key === 'repeatEveryDays' ? 1 : 0}
                value={form[field.key]}
                onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}
                className="max-w-xs"
              />
            </Field>
          ))}
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={save.isPending}>
              Save thresholds
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={run.isPending}
              onClick={() => run.mutate()}
            >
              Run the check now
            </Button>
            {save.isSuccess && <span className="text-sm text-emerald-700">Saved.</span>}
          </div>
        </form>
      </Card>
      {runResult && (
        <Card title="Last run" className="mt-4">
          <p className="text-sm text-slate-700">
            Checked {runResult.checked} open work order{runResult.checked === 1 ? '' : 's'};{' '}
            {runResult.reminded.length === 0
              ? 'nothing was stalled past its threshold (or was reminded recently).'
              : `sent ${runResult.reminded.length} reminder(s):`}
          </p>
          {runResult.reminded.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-sm text-slate-700">
              {runResult.reminded.map((item) => (
                <li key={`${item.workOrderNumber}-${item.stage}`}>
                  {item.workOrderNumber} — {item.stage.replace(/_/g, ' ')} ({item.recipients}{' '}
                  recipient{item.recipients === 1 ? '' : 's'})
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
